<script setup>
import { t, countLabel, message, schemaText } from '../lib/i18n.mjs'
import { computed, ref, watch, nextTick } from 'vue'
import AppIcon from './AppIcon.vue'
const props = defineProps({
  document: Object,
  schemas: Array,
  selection: Object,
  filterIds: Object,
  sourceNotice: String,
})
const emit = defineEmits(['annotate', 'select'])
const query = ref(''),
  scroll = ref(null)
const types = computed(() => Object.fromEntries(props.schemas.map((s) => [s.id, s])))
const paragraphs = computed(() => Object.fromEntries(props.document.paragraphs.map((p) => [p.id, p])))
const sentenceUnits = computed(() =>
  Object.fromEntries(props.document.units.flatMap((u) => u.sentenceIds.map((id) => [id, u]))),
)
const unitNumber = computed(() => Object.fromEntries(props.document.units.map((u, i) => [u.id, i + 1])))
const sentenceCount = computed(() => props.document.paragraphs.reduce((n, p) => n + p.sentences.length, 0))
const selectedParagraph = computed(() =>
  props.selection?.kind === 'paragraph'
    ? props.selection.id
    : props.document.units.find((u) => u.id === props.selection?.id)?.paragraphId,
)
const visibleBlocks = computed(() =>
  props.document.blocks.filter(
    (b) => !query.value || b.kind === 'heading' || b.text.toLowerCase().includes(query.value.toLowerCase()),
  ),
)
function unitStyle(unit) {
  const color = unit?.typeStatus === 'pending' ? '#d0d4db' : types.value[unit?.type]?.color || '#d0d4db'
  return { '--unit-color': color, '--unit-tint': unit ? `${color}65` : 'transparent' }
}
function activeSentence(sentence) {
  return props.selection?.kind === 'unit' && sentenceUnits.value[sentence.id]?.id === props.selection.id
}
watch(
  () => props.selection,
  async () => {
    query.value = ''
    await nextTick()
    const id =
      props.selection?.kind === 'unit'
        ? props.document.units.find((u) => u.id === props.selection.id)?.sentenceIds[0]
        : selectedParagraph.value
    if (!id) return
    const target = scroll.value?.querySelector(`[data-text-id="${id}"]`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  },
  { deep: true },
)
</script>
<template>
  <section class="text-panel pane">
    <header class="pane-header">
      <h2>{{ t('文本') }} <span class="heading-detail">{{ t('原文') }}</span></h2>
      <span class="count">{{ countLabel(document.paragraphs.length, '段落') }}</span>
    </header>
    <p v-if="sourceNotice" class="source-notice" role="status">{{ message(sourceNotice) }}</p>
    <div class="text-search">
      <AppIcon name="Search" :size="15" /><input
        v-model="query"
        type="search"
        :aria-label="t('检索源文本')"
        :placeholder="t('检索文本…')"
      /><span>{{ countLabel(sentenceCount, '句') }}</span>
    </div>
    <div ref="scroll" class="source-scroll">
      <template v-for="(block, i) in visibleBlocks" :key="i">
        <component
          :is="block.depth === 1 ? 'h1' : 'h3'"
          v-if="block.kind === 'heading'"
          class="source-heading"
          :class="{ 'document-title': block.depth === 1 }"
          >{{ block.text }}</component
        >
        <div
          v-else-if="block.kind === 'metadata'"
          class="source-meta"
          @dblclick="block.paragraphId && emit('annotate', { kind: 'paragraph', id: block.paragraphId })"
        >
          {{ block.text }}
        </div>
        <article
          v-else-if="paragraphs[block.paragraphId]"
          class="source-paragraph"
          :class="{
            'paragraph-selected': selection?.kind === 'paragraph' && selectedParagraph === block.paragraphId,
          }"
          :data-text-id="block.paragraphId"
          @dblclick.self="emit('annotate', { kind: 'paragraph', id: block.paragraphId })"
        >
          <button
            class="paragraph-label"
            :title="t('标注段落 {0}', { 0: block.paragraphId })"
            @dblclick.stop="emit('annotate', { kind: 'paragraph', id: block.paragraphId })"
            @click="emit('select', { kind: 'paragraph', id: block.paragraphId })"
          >
            {{ block.paragraphId }}
          </button>
          <div
            class="paragraph-copy"
            @dblclick.self="emit('annotate', { kind: 'paragraph', id: block.paragraphId })"
          >
            <span
              v-for="sentence in paragraphs[block.paragraphId].sentences"
              :key="sentence.id"
              class="sentence"
              :class="{
                selected: activeSentence(sentence),
                'filter-muted':
                  filterIds &&
                  !filterIds.has(sentenceUnits[sentence.id]?.id) &&
                  !filterIds.has(block.paragraphId),
              }"
              :style="unitStyle(sentenceUnits[sentence.id])"
              :data-text-id="sentence.id"
              :title="`${sentence.id} · ${sentenceUnits[sentence.id]?.id || ''} · ${schemaText(types[sentenceUnits[sentence.id]?.type])}`"
              @dblclick.stop="
                emit(
                  'annotate',
                  sentenceUnits[sentence.id]
                    ? { kind: 'unit', id: sentenceUnits[sentence.id].id }
                    : { kind: 'paragraph', id: block.paragraphId },
                )
              "
              @click.stop="emit('select', { kind: 'unit', id: sentenceUnits[sentence.id]?.id })"
              >{{ sentence.text
              }}<sup v-if="sentenceUnits[sentence.id]?.sentenceIds.at(-1) === sentence.id" class="unit-badge"
                >{{
                  document.engineVersion === 2 || sentenceUnits[sentence.id].typeStatus === 'pending'
                    ? 'U'
                    : types[sentenceUnits[sentence.id].type]?.prefix ||
                      types[sentenceUnits[sentence.id].type]?.english[0]
                }}{{ unitNumber[sentenceUnits[sentence.id].id] }}</sup
              ></span
            >
          </div>
        </article>
      </template>
      <div v-if="query && !visibleBlocks.some((b) => b.kind === 'paragraph')" class="empty-state">
        {{ t('没有匹配的段落') }}
      </div>
    </div>
    <footer class="pane-footer">
      <span><i class="status-dot" />{{ document.id }}.md</span><span>UTF-8</span>
    </footer>
  </section>
</template>
